import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  IoBrushOutline,
  IoChatbubbleEllipsesOutline,
  IoChevronDown,
  IoLanguage,
  IoPerson,
  IoPlay,
} from "react-icons/io5";
import {
  DIRECT_MESSAGE_PANEL_OPEN_EVENT,
  DIRECT_MESSAGE_UNREAD_EVENT,
  DIRECT_MESSAGE_UNREAD_GLOBAL_KEY,
} from "../../services/directMessageUi";
import ModernSidebar from "./ModernSidebar";
import {
  MODES,
  SELECTED_MODE_KEY,
  buildActivityRows,
  pickWaitingMatches,
  readSelectedMode,
} from "./LobbyModern";
import {
  doodleBackground,
  iconArt,
  toDataUri,
} from "./lobbyGalleryArt";
import "./lobbyModern.css";
import "./lobbyLive.css";

// Diseño «En vivo» del lobby, estilo galería de juegos: arriba los 4 modos en grande, debajo de
// cada modo sus fichas de 3 y 4 jugadores, y luego los botones principales; abajo, cada sección
// desglosada en fichas con su propio dibujo (puzzles, ranking e historial por modo, Aprender,
// comunidad y tu XIMA).

const COPY = {
  es: {
    greeting: (name) => `Hola, ${name}`,
    title: "Jugar",
    crumb: "Jugar",
    heading: "Modos de XIMA",
    intro:
      "Ciencia, Cienzar, Contrazar y Azar: de 2 a 4 jugadores, contra personas o contra el bot. Puzzles, rankings, torneos y todo XIMA en un solo lugar.",
    playNow: "JUGAR AHORA",
    howTitle: "¿Cómo funciona?",
    howText: "Elige un modo de juego, con cuántos jugadores quieres jugar y el tiempo de juego.",
    createSub: "Elige modo y reglas",
    sandbox: "Caja de arena",
    sandboxSub: "Crea tus puzzles",
    featured: "LOS 4 MODOS DE XIMA · PULSA UNO Y JUEGA YA",
    seeAll: "Ver todo ›",
    soon: "Próximamente",
    players: (n) => `${n} jugadores`,
    vs: (n) => `1 vs ${n}`,
    timeLabel: (n) => `${n} min`,
    timeTitle: (n) => `Partida de ${n} minutos`,
    createMatch: (mode, players, minutes) => `Crear partida de ${mode}: ${players} jugadores, ${minutes} min`,
    createError: "No se pudo crear la partida.",
    tickerEdit: "Editar",
    tickerEditTitle: "Texto del cintillo",
    tickerEditHint: "Déjalo vacío para volver al aviso del Campeonato Mundial.",
    tickerSave: "Guardar",
    tickerCancel: "Cancelar",
    tickerError: "No se pudo guardar el cintillo.",
    botTitle: "Jugar contra el bot",
    modeScience: "Ciencia",
    modeCienzar: "Cienzar",
    modeContrazar: "Contrazar",
    modeChance: "Azar",
    adultMode: "MODO",
    adultPlaying: (n) => `${n} jugando`,
    adultLess: "Quitar un jugador",
    adultMore: "Añadir un jugador",
    adultPlayers: (n) => `${n} jugadores`,
    adultTag: {
      ciencia: "Explora · Razona · Descubre",
      cienzar: "Ciencia + Azar = nuevos retos",
      sciencevsazar: "Estrategia frente al azar",
      azar: "La suerte también cuenta",
    },
    top: {
      bot: "Jugar con bot",
      watch: "Ver partidos",
      create: "Crear juego",
      find: "Buscar",
      puzzles: "Puzzles",
      tournaments: "Torneos",
      ranking: "Ranking",
      history: "Historial",
      championship: "Campeonato",
      design: "Modo diseñar",
      store: "Tienda",
      news: "Noticias",
      friends: "Amigos",
    },
    secPlay: "Jugar y competir",
    secModes: "Modos de juego por jugadores",
    secPuzzles: "Puzzles por modo",
    secRanking: "Ranking de posiciones, estadística y ayuda",
    secHistory: "Historial",
    secLearn: "Aprender",
    secSocial: "Redes sociales",
    socialYoutubeSub: "Canal oficial",
    getApp: "Descarga la app",
    getAppSub: "XIMA para Android",
    socialTiktokSub: "@yojuegoxima",
    socialVideoWide: "XIMA en menos de un minuto",
    socialVideoTall: "Cómo jugar XIMA en un minuto",
    secCommunity: "Competir y comunidad",
    secMine: "Tu XIMA",
    puzzlesOf: (mode) => `Puzzles de ${mode}`,
    puzzleCount: (n) => `${n} ${n === 1 ? "puzzle" : "puzzles"}`,
    allPuzzles: "Todos los puzzles",
    allPuzzlesSub: "Elige y resuelve",
    rankingGeneral: "Ranking general",
    rankingGeneralSub: "Todos los modos",
    rankingOf: (mode) => `Ranking de ${mode}`,
    rankingSub: "Los mejores",
    champions: "Campeones",
    championsSub: "Campeonato XIMA",
    historyOf: (mode) => `Historial de ${mode}`,
    historySub: "Tus partidas",
    historyAll: "Historial completo",
    historyAllSub: "Todos los modos",
    stats: "Estadísticas",
    statsSub: "Tu progreso",
    learn: {
      aprender: ["Aprender", "Todo para mejorar"],
      empezar: ["Empezar", "Tutorial en 3 min"],
      videos: ["Vídeos", "Mira y aprende"],
      reglas: ["Reglas", "Cómo se juega"],
      guias: ["Guías", "Estrategia"],
      libros: ["Libro", "El libro de XIMA"],
      ayuda: ["Ayuda", "Preguntas"],
      profesor: ["Profesor XIMA", "Juega con el entrenador"],
    },
    tournaments: "Torneos",
    tournamentsSub: "Compite y gana",
    championship: "Campeonato",
    champOpen: (seats) => `Inscripción abierta · ${seats}`,
    champRunning: (seats) => `En curso · ${seats}`,
    champJoined: (seats) => `Inscrito · ${seats}`,
    champIdle: "Pirámide y horarios",
    waiting: "Esperando rival",
    waitingSub: (n) => (n ? `${n} ${n === 1 ? "partida" : "partidas"}` : "Crea la tuya"),
    watch: "Ver partidos",
    watchSub: (n) => (n ? `${n} en vivo ahora` : "Repeticiones"),
    news: "Noticias",
    newsSub: "Lo último",
    friends: "Amigos",
    friendsSub: "Juega con ellos",
    design: "Diseñar tablero",
    designSub: "Tus bloques",
    store: "Tienda",
    storeSub: "Bloques y tableros",
    premium: "Premium",
    premiumSub: "Ventajas",
    find: "Buscar",
    findSub: "Partidas abiertas",
    settings: "Ajustes",
    settingsSub: "Cuenta e idioma",
    support: "Soporte",
    supportSub: "Te ayudamos",
    designMode: "Modo Diseñar",
    messages: "Mensajes",
    language: "Idioma",
  },
  en: {
    greeting: (name) => `Hi, ${name}`,
    title: "Play",
    crumb: "Play",
    heading: "XIMA modes",
    intro:
      "Science, Cienzar, Contrazar and Chance: 2 to 4 players, against people or the bot. Puzzles, rankings, tournaments and all of XIMA in one place.",
    playNow: "PLAY NOW",
    howTitle: "How does it work?",
    howText: "Pick a game mode, how many players you want to play with and the game time.",
    createSub: "Pick mode and rules",
    sandbox: "Sandbox",
    sandboxSub: "Build your puzzles",
    featured: "THE 4 XIMA MODES · PICK ONE AND PLAY",
    seeAll: "See all ›",
    soon: "Coming soon",
    players: (n) => `${n} players`,
    vs: (n) => `1 vs ${n}`,
    timeLabel: (n) => `${n} min`,
    timeTitle: (n) => `${n}-minute game`,
    createMatch: (mode, players, minutes) => `Create a ${mode} game: ${players} players, ${minutes} min`,
    createError: "The game could not be created.",
    tickerEdit: "Edit",
    tickerEditTitle: "Ticker text",
    tickerEditHint: "Leave it empty to go back to the World Championship notice.",
    tickerSave: "Save",
    tickerCancel: "Cancel",
    tickerError: "The ticker could not be saved.",
    botTitle: "Play against the bot",
    modeScience: "Science",
    modeCienzar: "Cienzar",
    modeContrazar: "Contrazar",
    modeChance: "Chance",
    adultMode: "MODE",
    adultPlaying: (n) => `${n} playing`,
    adultLess: "Remove a player",
    adultMore: "Add a player",
    adultPlayers: (n) => `${n} players`,
    adultTag: {
      ciencia: "Explore · Reason · Discover",
      cienzar: "Science + Chance = new challenges",
      sciencevsazar: "Strategy against chance",
      azar: "Luck counts too",
    },
    top: {
      bot: "Play the bot",
      watch: "Watch games",
      create: "Create game",
      find: "Find",
      puzzles: "Puzzles",
      tournaments: "Tournaments",
      ranking: "Ranking",
      history: "History",
      championship: "Championship",
      design: "Design mode",
      store: "Store",
      news: "News",
      friends: "Friends",
    },
    secPlay: "Play and compete",
    secModes: "Game modes by players",
    secPuzzles: "Puzzles by mode",
    secRanking: "Leaderboards, statistics and help",
    secHistory: "History",
    secLearn: "Learn",
    secSocial: "Social media",
    socialYoutubeSub: "Official channel",
    getApp: "Get the app",
    getAppSub: "XIMA for Android",
    socialTiktokSub: "@yojuegoxima",
    socialVideoWide: "XIMA in less than a minute",
    socialVideoTall: "How to play XIMA in a minute",
    secCommunity: "Compete and community",
    secMine: "Your XIMA",
    puzzlesOf: (mode) => `${mode} puzzles`,
    puzzleCount: (n) => `${n} ${n === 1 ? "puzzle" : "puzzles"}`,
    allPuzzles: "All puzzles",
    allPuzzlesSub: "Pick and solve",
    rankingGeneral: "Global ranking",
    rankingGeneralSub: "All modes",
    rankingOf: (mode) => `${mode} ranking`,
    rankingSub: "The best",
    champions: "Champions",
    championsSub: "XIMA Championship",
    historyOf: (mode) => `${mode} history`,
    historySub: "Your games",
    historyAll: "Full history",
    historyAllSub: "All modes",
    stats: "Statistics",
    statsSub: "Your progress",
    learn: {
      aprender: ["Learn", "Everything to improve"],
      empezar: ["Start", "3-minute tutorial"],
      videos: ["Videos", "Watch and learn"],
      reglas: ["Rules", "How to play"],
      guias: ["Guides", "Strategy"],
      libros: ["Book", "The XIMA book"],
      ayuda: ["Help", "Questions"],
      profesor: ["XIMA Coach", "Play with the coach"],
    },
    tournaments: "Tournaments",
    tournamentsSub: "Compete and win",
    championship: "Championship",
    champOpen: (seats) => `Registration open · ${seats}`,
    champRunning: (seats) => `In progress · ${seats}`,
    champJoined: (seats) => `Joined · ${seats}`,
    champIdle: "Bracket and schedule",
    waiting: "Waiting for a rival",
    waitingSub: (n) => (n ? `${n} ${n === 1 ? "game" : "games"}` : "Create yours"),
    watch: "Watch games",
    watchSub: (n) => (n ? `${n} live now` : "Replays"),
    news: "News",
    newsSub: "Latest",
    friends: "Friends",
    friendsSub: "Play with them",
    design: "Design board",
    designSub: "Your blocks",
    store: "Store",
    storeSub: "Blocks and boards",
    premium: "Premium",
    premiumSub: "Perks",
    find: "Find",
    findSub: "Open games",
    settings: "Settings",
    settingsSub: "Account and language",
    support: "Support",
    supportSub: "We help you",
    designMode: "Design mode",
    messages: "Messages",
    language: "Language",
  },
};

// Claves de cada modo en las otras pantallas (crear juego, bot, historial, puzzles, ranking).
const MODE_INFO = {
  ciencia: { art: "ciencia", create: "free", bot: "botlocalfree", history: "science", puzzles: "ciencia", ranking: "ciencia" },
  cienzar: { art: "cienzar", create: "cienzar", bot: "botlocalcienzar", history: "cienzar", puzzles: "cienzar", ranking: "cienzar" },
  sciencevsazar: { art: "contrazar", create: "sciencevsazar", bot: "botlocalsciencevsazar", history: "contrazar", puzzles: "", ranking: "sciencevsazar" },
  azar: { art: "azar", create: "dice", bot: "botlocaldice", history: "chance", puzzles: "azar", ranking: "azar" },
};

// true = la parte de abajo en blanco, como la galería original; false = todo oscuro.
const GALLERY_LIGHT = true;


// Relojes debajo de las fichas de 3 y 4 jugadores: 2 y 5 min bajo «1 vs 2», 10 y 30 bajo «1 vs 3».
const MATCH_MINUTES = { 3: [2, 5], 4: [10, 30] };
const DEFAULT_MATCH_MINUTES = 10;
const MATCH_MINUTES_STORAGE_KEY = "xima_lobby_live_minutes_v1";
const LAST_MATCH_STORAGE_KEY = "xima_lobby_live_last_match_v1";
// Diseño «Adulto»: jugadores elegidos en cada tarjeta de modo (2 a 4).
const ADULT_PLAYERS_STORAGE_KEY = "xima_lobby_adult_players_v1";
const readAdultPlayers = () => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(ADULT_PLAYERS_STORAGE_KEY) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};
const SHOW_CREATE_AND_FIND = false;

// Redes oficiales (las mismas que Aprender › Vídeos) y los 2 vídeos de «XIMA en un minuto».
const SOCIAL_LINKS = {
  youtube: "https://www.youtube.com/@YVMAxima",
  tiktok: "https://www.tiktok.com/@yojuegoxima",
  youtubeMinuteId: "bBmdNDj3PpA",
  tiktokMinuteId: "7685513970005921037",
};

// La misma APK que ofrece el aviso «Descargar app móvil» del menú (GameLayout).
const XIMA_APP_FILE_NAME = "app xima.apk";
const downloadApp = () => {
  const link = document.createElement("a");
  link.href = `/${encodeURIComponent(XIMA_APP_FILE_NAME)}`;
  link.download = XIMA_APP_FILE_NAME;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const openExternal = (url) => {
  window.open(url, "_blank", "noopener,noreferrer");
};

// Dibujos sencillos de las fichas YouTube y TikTok, con el color de cada red.
const socialArt = (network) =>
  network === "youtube"
    ? `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 160 100" preserveAspectRatio="xMidYMid slice"><rect width="160" height="100" fill="#1b1b1f"/><rect x="40" y="22" width="80" height="56" rx="16" fill="#ff0033"/><path d="M72 37v26l22-13z" fill="#fff"/></svg>`
    : `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 160 100" preserveAspectRatio="xMidYMid slice"><rect width="160" height="100" fill="#050505"/><g transform="translate(66 18)" font-family="Arial, sans-serif" font-weight="900" font-size="64"><text x="-3" y="58" fill="#25f4ee">♪</text><text x="3" y="62" fill="#fe2c55">♪</text><text x="0" y="60" fill="#fff">♪</text></g></svg>`;

const readMatchMinutes = () => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(MATCH_MINUTES_STORAGE_KEY) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const formatClockMinutes = (minutes) => `${String(minutes).padStart(2, "0")}:00`;

const puzzleModeOf = (baseMode) => {
  const value = String(baseMode || "").toLowerCase();
  if (value === "cienzar") return "cienzar";
  if (value === "dice" || value === "azar") return "azar";
  return "ciencia";
};

const initialsOf = (name) =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "?";

const playerKey = (value) => String(value || "").trim().toLowerCase();

// Partidos en vivo ordenados: el más mirado primero.
export const sortLiveMatches = (matches) =>
  (Array.isArray(matches) ? matches : [])
    .filter((match) => match && String(match.type || "live") !== "historical")
    .sort((a, b) => (Number(b.spectators) || 0) - (Number(a.spectators) || 0));

// Los dibujos no cambian: se generan una sola vez por idioma.
const artCache = {};
const cachedArt = (key, build) => {
  if (!artCache[key]) artCache[key] = build();
  return artCache[key];
};

const Art = ({ svg }) => <span className="xlg-art" aria-hidden="true" dangerouslySetInnerHTML={{ __html: svg }} />;

const LobbyLive = ({
  language,
  userName,
  userAvatar,
  userIds,
  rankLabel,
  cimaxTotalLabel,
  isAdmin,
  socialPending,
  badges = {},
  designChooser,
  newsTicker,
  tickerText,
  onSaveTicker,
  onTutorial,
  onPlayMode,
  onOpenProfile,
  onNavigate,
  actions,
  variant = "kids",
}) => {
  const copy = COPY[language] || (language === "es" ? COPY.es : COPY.en);
  const [liveMatches, setLiveMatches] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [puzzleCounts, setPuzzleCounts] = useState(null);
  const [playingMode, setPlayingMode] = useState("");
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [minutesByMode, setMinutesByMode] = useState(readMatchMinutes);
  const [adultPlayers, setAdultPlayers] = useState(readAdultPlayers);
  const isAdult = variant === "adult";
  const [creatingMatch, setCreatingMatch] = useState("");
  const [tickerDraft, setTickerDraft] = useState(null);
  const [savingTicker, setSavingTicker] = useState(false);

  // Móvil en vertical: «Jugar ahora» va fijo justo encima de la barra de abajo; se mide su alto.
  useEffect(() => {
    const nav = document.querySelector(".xima-game-sidebar");
    const root = document.documentElement;
    const update = () => {
      const fixedBottom = nav && window.getComputedStyle(nav).position === "fixed";
      root.style.setProperty("--xlv-nav-h", `${fixedBottom ? nav.offsetHeight : 0}px`);
    };
    update();
    const observer = nav && typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    if (observer) observer.observe(nav);
    window.addEventListener("resize", update);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", update);
      root.style.removeProperty("--xlv-nav-h");
    };
  }, []);

  // Cintillo de arriba: solo administradores lo editan; vacío vuelve al aviso por defecto.
  const saveTicker = async (event) => {
    event.preventDefault();
    if (!onSaveTicker || savingTicker) return;
    setSavingTicker(true);
    try {
      await onSaveTicker(String(tickerDraft || "").trim());
      setTickerDraft(null);
    } catch (error) {
      window.alert(error?.response?.data?.message || error?.message || copy.tickerError);
    } finally {
      setSavingTicker(false);
    }
  };

  useEffect(() => {
    let canceled = false;
    const load = () => {
      axios
        .get("/tournaments/live")
        .then((response) => {
          if (!canceled) setLiveMatches(Array.isArray(response?.data) ? response.data : []);
        })
        .catch(() => {});
      axios
        .get("/tournaments")
        .then((response) => {
          if (!canceled) setTournaments(Array.isArray(response?.data) ? response.data : []);
        })
        .catch(() => {});
      axios
        .get("/tournaments/lobby-activity")
        .then((response) => {
          if (!canceled) setActivity(Array.isArray(response?.data) ? response.data : []);
        })
        .catch(() => {});
    };
    load();
    const intervalId = window.setInterval(load, 8000);
    return () => {
      canceled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  // Cuántos puzzles hay de cada modo; los modos sin ninguno salen como «Próximamente».
  useEffect(() => {
    let canceled = false;
    axios
      .get("/tournaments/puzzles", { params: { limit: 300 } })
      .then((response) => {
        if (canceled) return;
        const counts = { ciencia: 0, cienzar: 0, azar: 0 };
        (Array.isArray(response?.data?.puzzles) ? response.data.puzzles : []).forEach((puzzle) => {
          counts[puzzleModeOf(puzzle?.freeBoxBaseMode)] += 1;
        });
        setPuzzleCounts(counts);
      })
      .catch(() => {});
    return () => {
      canceled = true;
    };
  }, []);

  useEffect(() => {
    const handleUnread = (event) =>
      setUnreadMessages(Math.max(0, Number(event?.detail?.total) || 0));
    window.addEventListener(DIRECT_MESSAGE_UNREAD_EVENT, handleUnread);
    setUnreadMessages(Math.max(0, Number(window[DIRECT_MESSAGE_UNREAD_GLOBAL_KEY]?.total) || 0));
    return () => window.removeEventListener(DIRECT_MESSAGE_UNREAD_EVENT, handleUnread);
  }, []);

  const live = useMemo(() => sortLiveMatches(liveMatches), [liveMatches]);
  const myIds = useMemo(() => new Set((userIds || []).map(playerKey)), [userIds]);
  const activityRows = useMemo(() => buildActivityRows(activity, myIds), [activity, myIds]);
  // JUGAR primero llena una sala pública del modo elegido y luego una partida abierta.
  const joinable = useMemo(
    () => [
      ...activityRows
        .filter((row) => row.canJoin && !row.place?.paid)
        .map((row) => ({
          entry: {
            _id: row.tournamentId,
            mode: row.mode,
            tournamentSequenceNumber: row.place?.number || 0,
          },
        })),
      ...pickWaitingMatches(tournaments, myIds).filter((item) => !item.isMine),
    ],
    [activityRows, myIds, tournaments]
  );

  const playMode = useCallback(
    async (mode) => {
      if (playingMode) return;
      try {
        window.localStorage.setItem(SELECTED_MODE_KEY, mode.id);
      } catch {
        // Sin almacenamiento solo se olvida el último modo elegido.
      }
      setPlayingMode(mode.id);
      try {
        await onPlayMode(mode.liveMode, joinable);
      } finally {
        setPlayingMode("");
      }
    },
    [joinable, onPlayMode, playingMode]
  );

  const selectedMode = MODES.find((mode) => mode.id === readSelectedMode()) || MODES[0];
  const modeName = (mode) => copy[mode.titleKey];

  const minutesFor = (mode) => {
    const stored = Number(minutesByMode[mode.key]);
    return [...MATCH_MINUTES[3], ...MATCH_MINUTES[4]].includes(stored) ? stored : DEFAULT_MATCH_MINUTES;
  };

  const playersFor = (mode) => {
    const stored = Number(adultPlayers[mode.key]);
    return stored >= 2 && stored <= 4 ? stored : 2;
  };

  const choosePlayers = (mode, players) => {
    const next = { ...adultPlayers, [mode.key]: Math.min(4, Math.max(2, players)) };
    setAdultPlayers(next);
    try {
      window.localStorage.setItem(ADULT_PLAYERS_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Sin almacenamiento los jugadores elegidos duran mientras la página esté abierta.
    }
  };

  const chooseMinutes = (mode, minutes) => {
    const next = { ...minutesByMode, [mode.key]: minutes };
    setMinutesByMode(next);
    try {
      window.localStorage.setItem(MATCH_MINUTES_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Sin almacenamiento el reloj elegido dura mientras la página esté abierta.
    }
  };

  // Botones de jugadores (1 vs 1, 3 y 4 jugadores): entra directo a jugar en el torneo fijo de
  // ese modo, esos jugadores y el reloj elegido. El servidor espera 10 s a otros humanos y luego
  // completa la mesa con bots.
  const createMatch = async (mode, players, minutesOverride) => {
    if (creatingMatch) return;
    const minutes = Number(minutesOverride) || minutesFor(mode);
    try {
      window.localStorage.setItem(LAST_MATCH_STORAGE_KEY, JSON.stringify({ mode: mode.key, players, minutes }));
    } catch {
      // Sin almacenamiento «Jugar ahora» no recuerda la última partida.
    }
    setCreatingMatch(`${mode.key}-${players}`);
    try {
      const response = await axios.get("/tournaments/fixed-quick", {
        params: { mode: MODE_INFO[mode.key].create, players, minutes },
      });
      const tournament = response?.data || null;
      const tournamentId = String(tournament?._id || "").trim();
      if (!tournamentId) throw new Error(copy.createError);
      await axios.post("/tournaments/join", { id: tournamentId });
      const sequenceNumber = Math.max(0, Number(tournament?.tournamentSequenceNumber) || 0);
      onNavigate(`/game/${tournamentId}?autoplay=1`, {
        state: {
          fromLobbySource: "lobby-live",
          returnTo: "/game/lobby",
          ...(sequenceNumber > 0
            ? { tournamentUiSequenceNumber: sequenceNumber, tournamentSequenceNumber: sequenceNumber }
            : {}),
        },
      });
    } catch (error) {
      window.alert(error?.response?.data?.message || error?.message || copy.createError);
    } finally {
      setCreatingMatch("");
    }
  };

  // Profesor XIMA: lo mismo que elegir «Entrenador de XIMA» en Jugar con bot y darle a jugar
  // (Ciencia 1 vs 1, dificultad injusto, trainerMode). Sin Premium va a la página Premium.
  const startCoach = async () => {
    if (creatingMatch) return;
    setCreatingMatch("coach");
    try {
      const access = await axios.get("/trainer/access").catch(() => null);
      if (access?.data?.allowed !== true) {
        onNavigate("/game/premium");
        return;
      }
      let havingTime = 30;
      try {
        const stored = JSON.parse(window.localStorage.getItem("xima_playbot_preferences_v1") || "{}");
        if ([15, 20, 30, 60].includes(Number(stored?.turnTime))) havingTime = Number(stored.turnTime);
      } catch {
        // Sin preferencias guardadas se usan 30 minutos, como en Jugar con bot.
      }
      const response = await axios.post("/tournaments", {
        tournamentName: "",
        mode: "botlocalfree",
        memberSize: 2,
        havingTime,
        botDifficulty: "injusto",
        allBotsMatch: false,
        trainerMode: true,
        duration: 1,
        startingTime: new Date().toISOString(),
        isQuickMatch: false,
        isIndependentMatch: false,
        hasMatchPassword: false,
        matchPassword: "",
        tournamentPassword: "",
        joinPassword: "",
        tournamentRewardValue: 0,
        tournamentWinnerPrizeValue: 0,
      });
      const tournamentId = String(response?.data?._id || "").trim();
      if (!tournamentId) throw new Error(copy.createError);
      onNavigate(`/game/${tournamentId}?autoplay=1&trainer=1`, {
        state: {
          fromLobbySource: "bot",
          source: "lobbyBotInterface",
          botGamePreferences: {
            mode: "botlocalfree",
            memberSize: 2,
            havingTime,
            botDifficulty: "injusto",
            allBotsMatch: false,
            trainerMode: true,
          },
        },
      });
    } catch (error) {
      window.alert(error?.response?.data?.message || error?.message || copy.createError);
    } finally {
      setCreatingMatch("");
    }
  };

  // «Jugar ahora»: repite la última partida creada desde estas fichas (modo, jugadores y minutos);
  // si no hay ninguna, juega el modo elegido como antes.
  const lastMatch = (() => {
    try {
      const stored = JSON.parse(window.localStorage.getItem(LAST_MATCH_STORAGE_KEY) || "null");
      const mode = MODES.find((item) => item.key === stored?.mode);
      const players = Number(stored?.players);
      const minutes = Number(stored?.minutes);
      if (!mode || !(players >= 2 && players <= 4) || !minutes) return null;
      return { mode, players, minutes };
    } catch {
      return null;
    }
  })();
  const playAgain = () =>
    lastMatch ? createMatch(lastMatch.mode, lastMatch.players, lastMatch.minutes) : playMode(selectedMode);

  const art = (key, build) => cachedArt(`${language}:${key}`, build);

  // img: portada en /lobby-galeria/ que sustituye al dibujo SVG.
  const tile = ({ key, svg, img, title, sub, onClick, tag, extra }) => (
    <div key={key} className="xlg-tile">
      <button
        type="button"
        className={`xlg-tile-btn${onClick ? "" : " is-soon"}`}
        onClick={onClick}
        aria-disabled={!onClick}
        title={title}
      >
        {img ? <img className="xlg-tile-img" src={`/lobby-galeria/${img}`} alt="" draggable="false" /> : <Art svg={svg} />}
        {tag ? <span className="xlg-tag">{tag}</span> : null}
        <span className="xlg-cap">
          <strong>{title}</strong>
          {sub ? <small>{sub}</small> : null}
        </span>
      </button>
      {extra}
    </div>
  );

  const section = (id, icon, title, items, onSeeAll, gridClass) => (
    <section key={id} className="xlg-sec">
      <h3>
        <span className="xlg-sec-dot" aria-hidden="true">
          {icon}
        </span>
        {title}
        {onSeeAll ? (
          <button type="button" onClick={onSeeAll}>
            {copy.seeAll}
          </button>
        ) : null}
      </h3>
      <div className={`xlg-grid${gridClass ? ` ${gridClass}` : ""}`}>{items}</div>
    </section>
  );

  // Botones principales (zona clara). image = portada en /lobby-galeria/btn-*.webp; si no hay, dibujo SVG.
  const topItems = [
    ["bot", "bot", null, actions.bot],
    ["watch", "ver-partidos", null, actions.watch, live.length || null],
    ["store", "tienda", null, actions.store],
    ["friends", "tile-amigos.webp", null, actions.friends, Number(badges.friends ?? socialPending) || null],
    ["tournaments", "torneos", null, actions.tournaments],
    ["championship", "campeonato", null, actions.championship],
    ["settings", "tile-ajustes.webp", null, actions.settings],
    ["premium", "tile-membresia.webp", null, actions.premium],
    ["design", "disenar", null, actions.design],
    ["news", "noticias", null, actions.news || (() => onNavigate("/game/news"))],
  ];

  const puzzleTiles = [
    // Caja de arena (donde se construyen los puzzles), igual que el botón del lobby 3D.
    tile({
      key: "pz-sandbox",
      img: "tile-caja-arena.webp",
      title: copy.sandbox,
      sub: copy.sandboxSub,
      onClick: () =>
        onNavigate("/game/play-sandbox", { state: { fromLobbySource: "sandbox", source: "lobbySandboxButton" } }),
    }),
    tile({
      key: "pz-all",
      img: "btn-puzzles.webp",
      title: copy.allPuzzles,
      sub: copy.allPuzzlesSub,
      onClick: actions.puzzles,
    }),
    ...MODES.map((mode) => {
      const puzzleKey = MODE_INFO[mode.key].puzzles;
      const count = puzzleKey && puzzleCounts ? puzzleCounts[puzzleKey] || 0 : null;
      const soon = !puzzleKey || count === 0;
      return tile({
        key: `pz-${mode.key}`,
        img: `tile-pz-${MODE_INFO[mode.key].art}.webp`,
        title: copy.puzzlesOf(modeName(mode)),
        sub: soon ? copy.soon : count === null ? "" : copy.puzzleCount(count),
        onClick: soon ? undefined : () => onNavigate(`/game/puzzles?modo=${puzzleKey}`),
      });
    }),
  ];

  const rankingTiles = [
    tile({ key: "rk-stats", img: "tile-estadisticas.webp", title: copy.stats, sub: copy.statsSub, onClick: actions.stats }),
    tile({
      key: "rk-all",
      img: "btn-ranking.webp",
      title: copy.rankingGeneral,
      sub: copy.rankingGeneralSub,
      onClick: actions.ranking,
    }),
    // Soporte y Ayuda ocupan el sitio de los rankings de Ciencia y Cienzar.
    tile({ key: "my-support", img: "tile-soporte.webp", title: copy.support, sub: copy.supportSub, onClick: actions.support }),
    tile({
      key: "ln-ayuda",
      img: "tile-ayuda.webp",
      title: copy.learn.ayuda[0],
      sub: copy.learn.ayuda[1],
      onClick: () => onNavigate("/game/learn?fromLobbySource=learn&tab=ayuda"),
    }),
    // Rankings por modo ocultos (2026-10-25); en su lugar, la app de Android.
    tile({ key: "rk-app", img: "tile-app.webp", title: copy.getApp, sub: copy.getAppSub, onClick: downloadApp }),
  ];

  const historyTiles = [
    tile({ key: "hs-all", img: "btn-historial.webp", title: copy.historyAll, sub: copy.historyAllSub, onClick: actions.history }),
    ...MODES.map((mode) =>
      tile({
        key: `hs-${mode.key}`,
        img: `tile-hist-${MODE_INFO[mode.key].art}.webp`,
        title: copy.historyOf(modeName(mode)),
        sub: copy.historySub,
        onClick: () => onNavigate(`/game/history?modo=${MODE_INFO[mode.key].history}`),
      })
    ),
  ];

  const learnTiles = [
    ["aprender", "book", "teal", "tile-aprender.webp"],
    ["empezar", "learnStart", "cienzar", "tile-tutorial.webp"],
    ["profesor", "book", "teal", "tile-profesor.webp"],
    ["reglas", "rules", "pink", "tile-reglas.webp"],
    ["guias", "guide", "slate", "tile-guias.webp"],
    ["libros", "book", "gold", "tile-libros.webp"],
  ].map(([tab, icon, tone, img]) =>
    tile({
      key: `ln-${tab}`,
      svg: img ? null : art(`ln-${tab}`, () => iconArt(icon, tone)),
      img,
      title: copy.learn[tab][0],
      sub: copy.learn[tab][1],
      onClick: () =>
        tab === "profesor"
          ? startCoach()
          : tab === "aprender"
          ? actions.learn()
          : tab === "empezar" && onTutorial
          ? onTutorial()
          : onNavigate(`/game/learn?fromLobbySource=learn${tab === "empezar" ? "" : `&tab=${tab}`}`),
    })
  );

  const socialTiles = [
    tile({
      key: "ln-videos",
      img: "tile-videos.webp",
      title: copy.learn.videos[0],
      sub: copy.learn.videos[1],
      onClick: () => onNavigate("/game/learn?fromLobbySource=learn&tab=videos"),
    }),
    tile({ key: "so-youtube", svg: art("so-youtube", () => socialArt("youtube")), title: "YouTube", sub: copy.socialYoutubeSub, onClick: () => openExternal(SOCIAL_LINKS.youtube) }),
    tile({ key: "so-tiktok", svg: art("so-tiktok", () => socialArt("tiktok")), title: "TikTok", sub: copy.socialTiktokSub, onClick: () => openExternal(SOCIAL_LINKS.tiktok) }),
    <div key="so-video-v" className="xlg-video is-tall">
      <iframe
        src={`https://www.tiktok.com/player/v1/${SOCIAL_LINKS.tiktokMinuteId}?rel=0&description=1&music_info=0`}
        title={copy.socialVideoTall}
        loading="lazy"
        allow="encrypted-media; fullscreen"
        allowFullScreen
      />
    </div>,
    <div key="so-video-h" className="xlg-video is-wide">
      <iframe
        src={`https://www.youtube-nocookie.com/embed/${SOCIAL_LINKS.youtubeMinuteId}?rel=0`}
        title={copy.socialVideoWide}
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    </div>,
  ];

  // Tu XIMA: Crear juego, Buscar, Premium, Ajustes y Soporte (lo demás ya está arriba).
  // Crear juego y Buscar ocultos por ahora (2026-10-25); poner true para que vuelvan.
  const mineTiles = !SHOW_CREATE_AND_FIND ? [] : [
    tile({ key: "my-create", svg: art("my-create", () => iconArt("create", "cienzar")), title: copy.top.create, sub: copy.createSub, onClick: actions.create }),
    tile({ key: "my-find", svg: art("my-find", () => iconArt("search", "purple")), title: copy.find, sub: copy.findSub, onClick: actions.find }),
  ];

  const doodle = cachedArt("doodle", () => toDataUri(doodleBackground()));

  return (
    <div className={`xlm-root xlv-root xlg-root${GALLERY_LIGHT ? " is-light" : ""}${isAdult ? " is-adult" : ""}`}>
      <ModernSidebar
        language={language}
        active="play"
        isAdmin={isAdmin}
        actions={actions}
        badges={{ ...badges, watch: badges.watch ?? live.length, friends: badges.friends ?? socialPending }}
      />

      <div className="xlm-main">
        <header className="xlm-topbar">
          <div className="xlm-heading">
            <span>{copy.greeting(userName)}</span>
            <h1>{copy.title}</h1>
          </div>
          <div className="xlm-top-actions">
            <button type="button" className="xlm-round" onClick={actions.design} aria-label={copy.designMode} title={copy.designMode}>
              <IoBrushOutline />
            </button>
            <button type="button" className="xlm-round" onClick={() => window.dispatchEvent(new CustomEvent(DIRECT_MESSAGE_PANEL_OPEN_EVENT))} aria-label={copy.messages}>
              <IoChatbubbleEllipsesOutline />
              {unreadMessages > 0 ? <b>{Math.min(99, unreadMessages)}</b> : null}
            </button>
            <button type="button" className="xlm-round" onClick={actions.language} aria-label={copy.language} title={copy.language}>
              <IoLanguage />
            </button>
            <button type="button" className="xlm-profile" onClick={onOpenProfile}>
              <span className="xlm-profile-avatar">
                {userAvatar ? <img src={userAvatar} alt="" /> : initialsOf(userName)}
              </span>
              <strong>{userName}</strong>
              <em>
                {rankLabel} · {cimaxTotalLabel}
              </em>
              <IoChevronDown aria-hidden="true" />
            </button>
          </div>
        </header>

        {newsTicker ? (
          <div className="xlm-ticker">
            {newsTicker}
            {isAdmin && onSaveTicker ? (
              <button type="button" className="xlg-ticker-edit" onClick={() => setTickerDraft(tickerText || "")}>
                {copy.tickerEdit}
              </button>
            ) : null}
          </div>
        ) : null}
        {tickerDraft !== null ? (
          <div className="xlg-ticker-modal" role="dialog" aria-modal="true" aria-label={copy.tickerEditTitle}>
            <form className="xlg-ticker-box" onSubmit={saveTicker}>
              <h3>{copy.tickerEditTitle}</h3>
              <textarea
                value={tickerDraft}
                onChange={(event) => setTickerDraft(event.target.value)}
                maxLength={300}
                rows={3}
                autoFocus
              />
              <small>{copy.tickerEditHint}</small>
              <div className="xlg-ticker-actions">
                <button type="button" onClick={() => setTickerDraft(null)} disabled={savingTicker}>
                  {copy.tickerCancel}
                </button>
                <button type="submit" className="is-primary" disabled={savingTicker}>
                  {copy.tickerSave}
                </button>
              </div>
            </form>
          </div>
        ) : null}

        <div className="xlg-board">
          <div className="xlg-band">
            <button type="button" className="xlg-band-icon is-avatar" onClick={onOpenProfile} aria-label={userName} title={userName}>
              {userAvatar ? <img src={userAvatar} alt="" /> : initialsOf(userName)}
            </button>
            <div className="xlg-band-text">
              <h2>
                <strong>{copy.howTitle}</strong>
              </h2>
              <p>{copy.howText}</p>
            </div>
            <button
              type="button"
              className="xlg-cta"
              onClick={playAgain}
              disabled={Boolean(playingMode) || Boolean(creatingMatch)}
              title={lastMatch ? copy.createMatch(modeName(lastMatch.mode), lastMatch.players, lastMatch.minutes) : modeName(selectedMode)}
            >
              <IoPlay aria-hidden="true" /> {copy.playNow}
            </button>
          </div>

          {isAdult ? (
            <div className="xlg-feat xla-feat">
              <div className="xlg-inner">
                <div className="xla-cards">
                  {MODES.map((mode) => {
                    const players = playersFor(mode);
                    const stop = (event) => event.stopPropagation();
                    return (
                      <div
                        key={mode.id}
                        role="button"
                        tabIndex={0}
                        aria-label={copy.createMatch(modeName(mode), players, minutesFor(mode))}
                        title={copy.createMatch(modeName(mode), players, minutesFor(mode))}
                        className={`xla-card is-${mode.tone}${creatingMatch === `${mode.key}-${players}` ? " is-busy" : ""}`}
                        onClick={() => createMatch(mode, players)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            createMatch(mode, players);
                          }
                        }}
                      >
                        {/* Portada del modo (ya trae el nombre); si falta, queda el degradado. */}
                        <img
                          className="xla-bg"
                          src={`/lobby-galeria/adulto-${MODE_INFO[mode.key].art}.webp?v=2`}
                          alt=""
                          draggable="false"
                          onError={(event) => {
                            event.currentTarget.style.visibility = "hidden";
                          }}
                        />
                        <div className="xla-top">
                          <span className="xla-live">
                            <i aria-hidden="true" />
                            {copy.adultPlaying(Number(badges.playingByMode?.[mode.liveMode]) || 0)}
                          </span>
                          <div className="xla-step" onClick={stop} title={copy.adultPlayers(players)}>
                            <button type="button" onClick={() => choosePlayers(mode, players - 1)} disabled={players <= 2} aria-label={copy.adultLess}>
                              −
                            </button>
                            <span aria-label={copy.adultPlayers(players)}>
                              {[0, 1, 2, 3].map((index) => (
                                <IoPerson key={index} className={index < players ? "is-on" : ""} aria-hidden="true" />
                              ))}
                            </span>
                            <button type="button" onClick={() => choosePlayers(mode, players + 1)} disabled={players >= 4} aria-label={copy.adultMore}>
                              +
                            </button>
                          </div>
                        </div>
                        <div className="xla-times" onClick={stop}>
                          {[...MATCH_MINUTES[3], ...MATCH_MINUTES[4]].map((minutes) => {
                            const selected = minutesFor(mode) === minutes;
                            return (
                              <button
                                key={minutes}
                                type="button"
                                className={selected ? "is-selected" : ""}
                                onClick={() => chooseMinutes(mode, minutes)}
                                aria-pressed={selected}
                                title={copy.timeTitle(minutes)}
                              >
                                {formatClockMinutes(minutes)}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="xlg-feat" style={{ backgroundImage: doodle }}>
              <div className="xlg-inner">
                <div className="xlg-big">
                  {MODES.map((mode) => (
                    <button
                      key={mode.id}
                      type="button"
                      className={`xlg-hero is-${mode.tone}${creatingMatch === `${mode.key}-2` ? " is-busy" : ""}`}
                      onClick={() => createMatch(mode, 2)}
                      disabled={Boolean(creatingMatch)}
                      aria-label={modeName(mode)}
                    >
                      <img className="xlg-hero-img" src={`/lobby-galeria/modo-${MODE_INFO[mode.key].art}.webp`} alt="" draggable="false" />
                      <span className="xlg-pill">👤 {Number(badges.playingByMode?.[mode.liveMode]) || 0}</span>
                      <span className="xlg-hero-play">
                        <IoPlay aria-hidden="true" />
                      </span>
                    </button>
                  ))}
                </div>
                <div className="xlg-small">
                  {MODES.flatMap((mode) =>
                    [3, 4].map((players) => (
                      <button
                        key={`mp-${mode.key}-${players}`}
                        type="button"
                        aria-label={`${modeName(mode)} · ${copy.vs(players - 1)}`}
                        className={`xlg-mini${creatingMatch === `${mode.key}-${players}` ? " is-busy" : ""}`}
                        onClick={() => createMatch(mode, players)}
                        disabled={Boolean(creatingMatch)}
                        title={copy.createMatch(modeName(mode), players, minutesFor(mode))}
                      >
                        <img className="xlg-hero-img" src={`/lobby-galeria/modo-${MODE_INFO[mode.key].art}-${players}.webp`} alt="" draggable="false" />
                      </button>
                    ))
                  )}
                </div>
                <div className="xlg-times">
                  {MODES.flatMap((mode) =>
                    [...MATCH_MINUTES[3], ...MATCH_MINUTES[4]].map((minutes) => {
                      const selected = minutesFor(mode) === minutes;
                      return (
                        <button
                          key={`tm-${mode.key}-${minutes}`}
                          type="button"
                          className={`xlg-time is-${mode.tone}${selected ? " is-selected" : ""}`}
                          onClick={() => chooseMinutes(mode, minutes)}
                          aria-pressed={selected}
                          title={copy.timeTitle(minutes)}
                        >
                          {formatClockMinutes(minutes)}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="xlg-body">
            <div className="xlg-inner">
              <section className="xlg-sec">
                <h3>
                  <span className="xlg-sec-dot" aria-hidden="true">
                    🎮
                  </span>
                  {copy.secPlay}
                </h3>
                <div className="xlg-main-btns">
                  {topItems.map(([id, image, build, onClick, count]) => (
                    <button
                      key={id}
                      type="button"
                      className="xlg-mini xlg-main-btn"
                      onClick={onClick}
                      title={copy.top[id]}
                      aria-label={copy.top[id]}
                    >
                      {image ? (
                        <img className="xlg-hero-img" src={`/lobby-galeria/${image.endsWith(".webp") ? image : `btn-${image}.webp`}`} alt="" draggable="false" />
                      ) : (
                        <>
                          <Art svg={art(`top-${id}`, build)} />
                          <span className="xlg-mini-cap">{copy.top[id]}</span>
                        </>
                      )}
                      {count ? <span className="xlg-pill is-small">{count}</span> : null}
                    </button>
                  ))}
                </div>
              </section>
              {section("puzzles", "🧩", copy.secPuzzles, puzzleTiles, actions.puzzles)}
              {section("learn", "🎓", copy.secLearn, learnTiles, actions.learn)}
              {section("history", "🕘", copy.secHistory, historyTiles, actions.history)}
              {section("ranking", "🏆", copy.secRanking, rankingTiles, actions.ranking)}
              {mineTiles.length ? section("mine", "💎", copy.secMine, mineTiles) : null}
              {section("social", "📣", copy.secSocial, socialTiles, null, "xlg-social")}
              {designChooser ? <div className="xlg-design-foot">{designChooser}</div> : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LobbyLive;
